import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import MonitorUtils, { MONITORING_COLLECTOR_TYPE } from "utils/MonitorUtils";
import { useStore } from 'zustand-lib/storeGlobal';
import useSellers from 'zustand-lib/useSellers';

export default function useTrackingMonitor(ref, product) {
  const sendEventCollector = useStore((state) => state.sendEventCollector);
  const getSellerByCode = useSellers((state) => state.getSellerByCode);
  const router = useRouter();
  const isSendEvent = useRef(false);
  const observerRef = useRef(null);

  const sendEvent = useCallback(async () => {
    const sellerInfo = await getSellerByCode({ code: product?.sellerCode, tags: product?.tags || [] });
    sendEventCollector(MonitorUtils.sendSKUEvent(MONITORING_COLLECTOR_TYPE.SKU_IMPRESSION, { ...product, sellerInfo, blockCode: product?.blockCode || router.query.blockCode }, router.pathname, true));
  }, [product, getSellerByCode, sendEventCollector, router.query.blockCode, router.pathname]);

  // Use IntersectionObserver to track when product comes into view
  // This replaces the previous useOnScreen + useHover + 2-second timeout logic
  useEffect(() => {
    if (!ref?.current || !product || isSendEvent.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isSendEvent.current) {
          sendEvent();
          isSendEvent.current = true;
          observer.unobserve(ref.current);
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the product is visible
    );

    observer.observe(ref.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current && ref.current) {
        observerRef.current.unobserve(ref.current);
      }
    };
  }, [product, ref, sendEvent]);
}
